import { Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { handleLoginSuccess, useLogin } from '~/services/api/auth';
import { styled } from '~/themes';
import { UnifiedPublicLayout } from '~/layout/UnifiedPublicLayout';
import { Button, theme } from 'bs-unified-ui';
import { Field } from '~/components/common/Formik';
import { Stack } from '@mui/system';
import { BroadcastEvents, channel } from '~/lib/broadcast';
import { useUnifiedAuthContext } from '~/providers/UnifiedAuthProvider';
import { useEffect, useState } from 'react';
import VerifyCode2FA from '~/pages/Auth/VerifyCode2FA';
import { use2Fa } from '~/hooks/use2Fa';
import { TextLink } from '~/components/common/TextLink';
import { UnifiedLoginSchema } from './schema';

type LoginProps = {};

export const StyledLoginForm = styled(Form)(() => ({
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
}));

export const UnifiedLogin: React.FC<LoginProps> = () => {
  const { isLoginLoading } = useUnifiedAuthContext();
  const [errorMsg, setErrorMsg] = useState<string>('');

  const {
    handleEnable2FA,
    handleSendCode,
    loadingSendCode,
    phone,
    handleVerifyCode,
    enable2FA,
    handleCancel,
    loadingVerify,
    errorMsg: errorMsg2Fa,
    clearErrorMsg,
  } = use2Fa({
    recaptchaId: 'recaptcha-container-id',
    onLoginSuccess: async () => {
      await handleLoginSuccess({
        isShowAlert: false,
        isInvalidateQueries: true,
      });
    },
  });

  const { mutate: loginMutation, isPending: isLoading } = useLogin({
    options: {
      isShowAlert: false,
      isInvalidateQueries: true,
    },
    onSuccess: () => {},
    onError: (errorString, error) => {
      if (error?.code === 'auth/multi-factor-auth-required') {
        handleEnable2FA(error);
      } else setErrorMsg(errorString);
    },
  });

  const clearAllErrorMsg = () => {
    clearErrorMsg();
    setErrorMsg('');
  };

  useEffect(() => {
    channel.postMessage({
      type: BroadcastEvents.SyncLoginState,
      payload: isLoading,
    });
  }, [isLoading]);

  const renderVerifyCode = () => (
    <VerifyCode2FA
      phone={phone}
      onComplete={handleVerifyCode}
      onClickReSend={handleSendCode}
      loading={isLoading || isLoginLoading || loadingSendCode || loadingVerify}
      error={errorMsg2Fa}
      clearError={clearErrorMsg}
    />
  );

  const renderLoginForm = () => (
    <Formik
      initialValues={{
        email: '',
        password: '',
      }}
      enableReinitialize
      validationSchema={UnifiedLoginSchema}
      onSubmit={(values) => {
        clearAllErrorMsg();
        loginMutation(values);
      }}
    >
      {() => (
        <StyledLoginForm>
          <Typography variant='h1' className='headerContainer'>
            Login to your account
          </Typography>
          <Stack spacing={2}>
            <Field.UnifiedTextField
              name='email'
              label='Email'
              fullWidth
              onChange={() => clearAllErrorMsg()}
            />
            <Field.UnifiedTextField
              name='password'
              type='password'
              label='Password'
              fullWidth
              onChange={() => clearAllErrorMsg()}
            />
            <Typography
              sx={{
                lineHeight: 1.66,
                color: '#F24822',
                mt: '10px !important',
                fontSize: 14,
              }}
            >
              {errorMsg || errorMsg2Fa}
            </Typography>
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
              fontSize: 14,
              color: `${theme.foreground.black} !important`,
            }}
          >
            <TextLink to='https://bs.int.breadstack.io/reset-password'>
              <Typography variant='fs14' component='p'>
                Forgot Password
              </Typography>
            </TextLink>
          </Stack>
          <Button
            type='submit'
            sx={{ mt: 2 }}
            loading={isLoading || isLoginLoading || loadingSendCode}
          >
            Login
          </Button>
        </StyledLoginForm>
      )}
    </Formik>
  );

  return (
    <UnifiedPublicLayout hasButtonBack={enable2FA} onBack={handleCancel}>
      {enable2FA ? renderVerifyCode() : renderLoginForm()}
      <div id='recaptcha-container-id' />
    </UnifiedPublicLayout>
  );
};
